<template>
  <v-card>
    <loader v-if="fetchContactPersonPending"/>
    <template v-else>
      <v-card-text class="pa-5">
        <v-container class="pa-0" grid-list-md>
          <form>
            <v-row>
              <v-col>
                <v-autocomplete
                  :items="salutationItems"
                  v-model="form.sex"
                  :label="$t('texts.salutation')"
                  item-text="text"
                  item-value="value"
                  required
                  :error-messages="errors.collect('salutation')"
                  v-validate="'required'"
                  data-vv-name="salutation"
                  :data-vv-as="$t('texts.salutation')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col :cols="12" :md="6">
                <v-text-field
                  name="contactPersonFirstName"
                  :label="$t('texts.firstName')"
                  v-model="form.first_name"
                  required
                  :error-messages="errors.collect('firstName')"
                  v-validate="'required'"
                  data-vv-name="firstName"
                  :data-vv-as="$t('texts.firstName')"
                />
              </v-col>
              <v-col :cols="12" :md="6">
                <v-text-field
                  name="contactPersonLastName"
                  :label="$t('texts.lastName')"
                  v-model="form.last_name"
                  required
                  :error-messages="errors.collect('lastName')"
                  v-validate="'required'"
                  data-vv-name="lastName"
                  :data-vv-as="$t('texts.lastName')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  name="contactPersonEmail"
                  :label="$t('texts.email')"
                  v-model="form.email"
                  required
                  :error-messages="errors.collect('email')"
                  v-validate="'required|email'"
                  data-vv-name="email"
                  :data-vv-as="$t('texts.email')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  name="contactPersonDirectCall"
                  :label="$t('texts.directCall')"
                  v-model="form.phone"
                  v-mask="mask.phoneNumber"
                  required
                  :error-messages="errors.collect('directCall')"
                  v-validate="{
                    required: true,
                    regex: /^(\+41\ )[1-9]{2}\ [0-9]{3}\ [0-9]{2}\ [0-9]{2}$/,
                  }"
                  :hint="form.phone === '' ? '+41 12 345 67 89' : ''"
                  :persistent-hint="form.phone === ''"
                  data-vv-name="directCall"
                  :data-vv-as="$t('texts.directCall')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  name="contactPersonPosition"
                  :label="$t('texts.jobPosition.label')"
                  v-model="form.position"
                  required
                  :error-messages="errors.collect('position')"
                  v-validate="'required'"
                  data-vv-name="position"
                  :data-vv-as="$t('texts.jobPosition.label')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-autocomplete
                  :items="languageItems"
                  v-model="form.language"
                  :label="$tc('texts.language.label', 1)"
                  item-text="text"
                  item-value="value"
                  required
                  :error-messages="errors.collect('language')"
                  v-validate="'required'"
                  data-vv-name="language"
                  :data-vv-as="$tc('texts.language.label', 1)"
                />
              </v-col>
            </v-row>
          </form>
        </v-container>
      </v-card-text>
      <v-divider/>
      <v-card-actions class="px-5 py-3">
        <cta-buy-subscription/>
        <v-spacer/>
        <v-btn
          color="primary"
          class="white--text"
          @click="validateBeforeSubmit()"
          :disabled="storeContactPersonPending"
          :loading="storeContactPersonPending"
        >
          {{ $t('texts.save') }}
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import CtaBuySubscription from '@/components/_molecules/company/CTABuySubscription.vue';

const { mapState: mapCompanyProfileState, mapActions: mapCompanyProfileActions } = createNamespacedHelpers('company/profile');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'CompanyProfileContactPerson',
  components: {
    CtaBuySubscription,
  },
  data() {
    return {
      form: {
        id: null,
        sex: null,
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        position: '',
        language: '',
      },
      mask: {
        phoneNumber: '+41 ## ### ## ##',
      },
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  created() {
    this.fetchContactPerson(this.selectedCompanyId).then(() => {
      Object.assign(this.form, this.contactPerson);
    });
  },
  computed: {
    ...mapCompanyProfileState([
      'fetchContactPersonPending',
      'storeContactPersonPending',
      'contactPerson',
    ]),
    salutationItems() {
      const items = [
        { value: 1, text: this.$i18n.t('texts.salutationMr') },
        { value: 2, text: this.$i18n.t('texts.salutationMrs') },
      ];
      return items;
    },
    languageItems() {
      const items = [
        { value: 'de', text: this.$i18n.t('texts.language.de') },
        { value: 'it', text: this.$i18n.t('texts.language.it') },
        { value: 'en', text: this.$i18n.t('texts.language.en') },
        { value: 'fr', text: this.$i18n.t('texts.language.fr') },
      ];
      return items;
    },
    selectedCompanyId() {
      return this.$route.params.companyId;
    },
  },
  methods: {
    ...mapCompanyProfileActions([
      'fetchContactPerson',
      'storeContactPerson',
    ]),
    ...mapMessageActions([
      'showMessageValidateError',
    ]),
    validateBeforeSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const payload = {
            companyId: this.selectedCompanyId,
            form: this.form,
          };
          this.storeContactPerson(payload);
        } else {
          this.showMessageValidateError();
        }
      });
    },
  },
};
</script>
